import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fieldCls =
  "bg-obsidian border-hairline text-ivory placeholder:text-slate-600 focus-visible:ring-gold/60 h-11";

export const BookingForm = () => {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "",
    address: "",
    listing_url: "",
    property_type: "",
    package: searchParams.get("forfait") || "",
    desired_date: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/requests`, form);
      setReference(data.id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(t.form.error);
    } finally {
      setLoading(false);
    }
  };

  if (reference) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="bg-midnight border border-gold/40 rounded-lg p-10 sm:p-14 text-center"
        data-testid="booking-success"
      >
        <CheckCircle2 className="w-14 h-14 text-gold mx-auto" strokeWidth={1.2} />
        <h2 className="mt-6 font-serif text-3xl sm:text-4xl font-semibold text-ivory">{t.form.successTitle}</h2>
        <p className="mt-4 text-slate-300">
          {t.form.successText}{" "}
          <span className="font-mono text-gold text-sm" data-testid="booking-reference">
            {reference.slice(0, 8).toUpperCase()}
          </span>
        </p>
        <p className="mt-2 text-sm text-slate-400">{t.form.successFollow}</p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
          <button
            onClick={() => setReference(null)}
            data-testid="booking-new-request"
            className="border border-ivory/25 text-ivory text-sm font-medium px-7 py-3 rounded-full hover:border-gold hover:text-gold transition-colors"
          >
            {t.form.newRequest}
          </button>
          <Link
            to="/"
            data-testid="booking-back-home"
            className="bg-gold text-obsidian text-sm font-semibold px-7 py-3 rounded-full hover:bg-gold-light transition-colors"
          >
            {t.form.backHome}
          </Link>
        </div>
      </motion.div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="booking-form"
      className="bg-midnight border border-hairline rounded-lg p-7 sm:p-10"
    >
      <div className="grid sm:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="bk-name" className="text-xs uppercase tracking-widest text-slate-400">{t.form.name} *</Label>
          <Input id="bk-name" data-testid="booking-name-input" required minLength={2} className={`mt-2 ${fieldCls}`} value={form.name} onChange={set("name")} />
        </div>
        <div>
          <Label htmlFor="bk-email" className="text-xs uppercase tracking-widest text-slate-400">{t.form.email} *</Label>
          <Input id="bk-email" data-testid="booking-email-input" type="email" required className={`mt-2 ${fieldCls}`} value={form.email} onChange={set("email")} />
        </div>
        <div>
          <Label htmlFor="bk-phone" className="text-xs uppercase tracking-widest text-slate-400">{t.form.phone} *</Label>
          <Input id="bk-phone" data-testid="booking-phone-input" type="tel" required minLength={5} className={`mt-2 ${fieldCls}`} value={form.phone} onChange={set("phone")} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-400">{t.form.city} *</Label>
          <Select required value={form.city} onValueChange={set("city")}>
            <SelectTrigger data-testid="booking-city-select" className={`mt-2 ${fieldCls}`}>
              <SelectValue placeholder={t.form.selectPlaceholder} />
            </SelectTrigger>
            <SelectContent className="bg-surface border-hairline text-ivory">
              {["Paris", "Bâle", "Sarajevo", "Prizren"].map((c) => (
                <SelectItem key={c} value={c} data-testid={`booking-city-${c.toLowerCase()}`}>{c}</SelectItem>
              ))}
              <SelectItem value="Autre" data-testid="booking-city-autre">{t.form.cityOther}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="bk-address" className="text-xs uppercase tracking-widest text-slate-400">{t.form.address} *</Label>
          <Input id="bk-address" data-testid="booking-address-input" required minLength={3} className={`mt-2 ${fieldCls}`} value={form.address} onChange={set("address")} />
        </div>
        <div>
          <Label htmlFor="bk-listing" className="text-xs uppercase tracking-widest text-slate-400">{t.form.listing}</Label>
          <Input id="bk-listing" data-testid="booking-listing-input" type="url" placeholder="https://…" className={`mt-2 ${fieldCls}`} value={form.listing_url} onChange={set("listing_url")} />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-400">{t.form.type} *</Label>
          <Select required value={form.property_type} onValueChange={set("property_type")}>
            <SelectTrigger data-testid="booking-type-select" className={`mt-2 ${fieldCls}`}>
              <SelectValue placeholder={t.form.selectPlaceholder} />
            </SelectTrigger>
            <SelectContent className="bg-surface border-hairline text-ivory">
              {t.form.types.map((ty) => (
                <SelectItem key={ty} value={ty} data-testid={`booking-type-${ty.toLowerCase().replace(/\s/g, "-")}`}>{ty}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-400">{t.form.packageLabel} *</Label>
          <Select required value={form.package} onValueChange={set("package")}>
            <SelectTrigger data-testid="booking-package-select" className={`mt-2 ${fieldCls}`}>
              <SelectValue placeholder={t.form.selectPlaceholder} />
            </SelectTrigger>
            <SelectContent className="bg-surface border-hairline text-ivory">
              {t.packages.items.map((p) => (
                <SelectItem key={p.id} value={p.id} data-testid={`booking-package-${p.id}`}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="bk-date" className="text-xs uppercase tracking-widest text-slate-400">{t.form.date}</Label>
          <Input id="bk-date" data-testid="booking-date-input" type="date" className={`mt-2 ${fieldCls} [color-scheme:dark]`} value={form.desired_date} onChange={set("desired_date")} />
        </div>
        <div className="sm:col-span-2">
          <Label htmlFor="bk-message" className="text-xs uppercase tracking-widest text-slate-400">{t.form.message}</Label>
          <Textarea
            id="bk-message"
            data-testid="booking-message-input"
            rows={4}
            placeholder={t.form.messagePlaceholder}
            className="mt-2 bg-obsidian border-hairline text-ivory placeholder:text-slate-600 focus-visible:ring-gold/60"
            value={form.message}
            onChange={set("message")}
          />
        </div>
      </div>

      <p className="mt-6 text-xs leading-relaxed text-slate-500 border-l-2 border-gold/40 pl-4">{t.form.disclaimer}</p>

      <button
        type="submit"
        disabled={loading}
        data-testid="booking-submit-button"
        className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-gold text-obsidian font-bold text-sm tracking-[0.08em] px-8 py-4 rounded-full hover:bg-gold-light transition-colors duration-300 disabled:opacity-60"
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {loading ? t.form.sending : t.form.submit}
      </button>
    </form>
  );
};
